import React, { useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { confirmDialog } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellId, CellText, CellPlate, CellMoney } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { Pill, formatPlate } from './ui';
import { useRowLinks, stickyTop } from './jobLinks';
import { paidOn, howPaid, payerText } from './rtaPayments';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';

// Payments (owner, 15 Oct 2026: "زر فى الفاين اسمه مدفوعات"): the RTA receipt PDFs a user holds - above all the fines
// paid on RTA's public fines page, which never show in the company account's history the engine reads every week.
// The PDFs are read and matched first (nothing saved); only "Save" and its confirmation keep them, in the same store
// as the weekly read. Who paid them is chosen first (owner, 16 Oct 2026: RTA prints the company on every receipt,
// whoever paid - "ملفات الشركة لوحدها وملفات العميل لوحدها"): the company's, or the customer's - the receipts a customer
// sent us - whose fines Save marks paid by the customer ("تتعلم عميل اللى دفع لوحدها"). Nothing goes to Ejar or RTA.

const MAX_FILES = 20;          // the engine's limits (engine/docs/RTA_PAYMENTS_SPEC.md): said here before the upload
const MAX_BYTES = 12 * 1024 * 1024;

const MATCH = {
    engine: { tone: 'green', label: 'Matched' },
    unknown: { tone: 'red', label: 'Not in Yelo' },
    amount_diff: { tone: 'amber', label: 'Amount differs' },
    impound_matched: { tone: 'green', label: 'Impound paid' },
    impound_review: { tone: 'amber', label: 'Review: several fines' },
    impound_unknown: { tone: 'gray', label: 'No fine found' },
};

// every line in exactly one group; a receipt Yelo keeps already shows its lines under "Already saved" only
const GROUPS = [
    { key: 'matched', title: 'Matched fines', subtitle: 'The fine is in Yelo with the same amount (or that amount less the AED 20 knowledge fee Yelo keeps on Dubai Police and RTA parking fines).' },
    { key: 'unknown', title: 'Not in Yelo', subtitle: 'No fine in Yelo carries this ticket number: a car of another company, or a fine RTA never listed on our account.' },
    { key: 'diff', title: 'Amount differs', subtitle: 'The fine is in Yelo with another amount.' },
    { key: 'impounds', title: 'Impounds', subtitle: 'An impound line carries no ticket: it is matched to the fine of the same car at the same minute. Several fines at that minute are left for a person to judge.' },
    { key: 'saved', title: 'Already saved', subtitle: "These receipts are in Yelo already (read from the company RTA account or uploaded before). As the company's: nothing new to save; as the customer's: Save marks them paid by the customer." },
];
const groupOf = (line) => {
    if (line.alreadyStored) return 'saved';
    if (line.kind === 'impound') return 'impounds';
    if (line.match === 'engine') return 'matched';
    if (line.match === 'amount_diff') return 'diff';
    return 'unknown';
};

// the engine fines an impound line is put against: several for 'impound_review' (owner, 15 Oct 2026) - each its own
// link, never one link with "a, b" (no fine has that number). `engine_tickets` is the list; an older engine answer sent
// only `engine_ticket` joined with ", ".
const reviewTickets = (l) => (Array.isArray(l.engine_tickets) && l.engine_tickets.length
    ? l.engine_tickets : String(l.engine_ticket || '').split(',')).map((t) => String(t ?? '').trim()).filter(Boolean);

const sizeText = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const isPdf = (f) => /\.pdf$/i.test(f.name || '') || f.type === 'application/pdf';
const fileKey = (f) => `${f.name}|${f.size}|${f.lastModified}`;

const LinesTable = ({ rows, FineLink, ContractLink }) => (
    <TableContainer maxHeight={320}>
        <Table sticky>
            <TableHead><TableRow>
                <TableHeader>#</TableHeader><TableHeader>Fine</TableHeader><TableHeader>Plate</TableHeader><TableHeader>Offence on</TableHeader>
                <TableHeader>Offence</TableHeader><TableHeader>Receipt</TableHeader><TableHeader>Yelo</TableHeader><TableHeader>Result</TableHeader>
                <TableHeader>Contract</TableHeader><TableHeader>Paid on</TableHeader><TableHeader>Receipt ref</TableHeader>
            </TableRow></TableHead>
            <TableBody>{rows.map((l, i) => {
                const m = MATCH[l.match] || { tone: 'gray', label: l.match || '—' };
                const ticket = l.engine_ticket || l.ticket;
                const several = l.match === 'impound_review' ? reviewTickets(l) : null;
                return (
                    <TableRow key={`${l.fileIndex}-${l.lineIndex}`}>
                        <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                        {/* a fine Yelo has opens its window; a ticket Yelo does not know is only its number */}
                        <TableCell>{several ? (several.length ? <span className="inline-flex flex-wrap justify-center gap-1">{several.map((t) => <FineLink key={t} ticket={t} />)}</span> : '—')
                            : l.engine_ticket ? <FineLink ticket={l.engine_ticket} /> : ticket ? <CellId>{ticket}</CellId> : '—'}</TableCell>
                        <TableCell><CellPlate>{formatPlate(l.plate) || '—'}</CellPlate></TableCell>
                        <TableCell><span className="ds-cell-date">{paidOn(l.offence_at) || '—'}</span></TableCell>
                        <TableCell><CellText max={220}>{l.offence || '—'}</CellText></TableCell>
                        <TableCell><CellMoney value={Number(l.amount || 0)} currency="AED" /></TableCell>
                        <TableCell>{l.engine_amount !== undefined && l.engine_amount !== null ? <CellMoney value={Number(l.engine_amount)} currency="AED" /> : '—'}</TableCell>
                        <TableCell><Pill tone={m.tone} size="sm" title={l.engine_status ? `Yelo: ${l.engine_status}` : undefined}>{m.label}</Pill></TableCell>
                        <TableCell>{l.contract_no ? <ContractLink contract={l.contract_no} plate={l.plate} /> : '—'}</TableCell>
                        <TableCell><span className="ds-cell-date">{paidOn(l.file.paid_at) || '—'}</span></TableCell>
                        <TableCell><span title={l.file.name}><CellId>{l.file.ref || '—'}</CellId></span></TableCell>
                    </TableRow>
                );
            })}</TableBody>
        </Table>
    </TableContainer>
);

export default function RtaPaymentsModal({ onClose, onDone }) {
    const [files, setFiles] = useState([]);
    const [paidBy, setPaidBy] = useState('');         // who paid these receipts: 'company' | 'customer', chosen first
    const [preview, setPreview] = useState(null);     // the read of the chosen files (commit=false): nothing saved
    const [saved, setSaved] = useState(null);         // the answer of the save (commit=true)
    const [busy, setBusy] = useState('');             // 'read' | 'save'
    const [error, setError] = useState(null);
    const changed = useRef(false);
    const { FineLink, ContractLink, modals } = useRowLinks();
    const { hasPermission } = usePermissions();
    const canExport = hasPermission(PERMISSIONS.export_traffic_fines);

    // a new choice of files is a new read: a preview never stands for files it did not read
    const reset = () => { setPreview(null); setSaved(null); setError(null); };
    const add = (picked) => {
        if (!picked.length) return;
        setFiles((cur) => {
            const have = new Set(cur.map(fileKey));
            return [...cur, ...picked.filter((f) => !have.has(fileKey(f)))];
        });
        reset();
    };
    const remove = (k) => { setFiles((cur) => cur.filter((f) => fileKey(f) !== k)); reset(); };

    const notPdf = files.filter((f) => !isPdf(f));
    const tooBig = files.filter((f) => f.size > MAX_BYTES);
    const problem = files.length > MAX_FILES ? `At most ${MAX_FILES} PDFs at a time - remove ${files.length - MAX_FILES}.`
        : notPdf.length ? `Not a PDF: ${notPdf.map((f) => f.name).join(', ')}.`
            : tooBig.length ? `Over 12 MB: ${tooBig.map((f) => f.name).join(', ')}.` : '';

    const read = async () => {
        setBusy('read'); reset();
        try { setPreview(await finesService.uploadRtaReceipts(files, false, paidBy)); } catch (e) { setError(errorMessage(e)); }
        setBusy('');
    };
    const choosePaidBy = (who) => { setPaidBy(who); reset(); };     // a new choice is a new read

    const readFiles = preview?.files || [];
    const lines = readFiles.flatMap((f, fileIndex) => (f.ok ? (f.lines || []).map((l, lineIndex) => ({
        ...l, file: f, fileIndex, lineIndex, alreadyStored: !!f.already_stored,
    })) : []));
    const groups = GROUPS.map((g) => ({ ...g, rows: lines.filter((l) => groupOf(l) === g.key) })).filter((g) => g.rows.length);
    const failed = readFiles.filter((f) => !f.ok);
    const newRefs = [...new Set(readFiles.filter((f) => f.ok && !f.already_stored).map((f) => f.ref || f.name))];
    // the customer's: every receipt read is saved or marked - one the weekly read stored as the company's too
    const customer = paidBy === 'customer';
    const toSave = customer ? [...new Set(readFiles.filter((f) => f.ok).map((f) => f.ref || f.name))] : newRefs;
    // a customer sends his own fines: a receipt paying fines of several contracts is most likely the company's - said
    // before Save, never refused (16 Oct 2026 review: a batch chosen as the customer's would mark every fine on it)
    const manyContracts = customer ? readFiles.filter((f) => f.ok).map((f) => ({
        name: f.name,
        contracts: [...new Set((f.lines || []).filter((l) => l.kind === 'fine' && l.contract_no).map((l) => l.contract_no))],
    })).filter((x) => x.contracts.length > 1) : [];
    const s = preview?.summary || {};

    const save = async () => {
        if (!toSave.length) return;
        if (!(await confirmDialog({
            title: customer ? "Save as the customer's payments?" : 'Save the receipts?',
            message: customer
                ? `Save ${toSave.length} receipt(s) as paid by the CUSTOMER? Their fines in Yelo are marked "Customer": never billed to the customer and never registered on Ejar. Nothing is sent to Ejar or RTA.`
                : `Save ${newRefs.length} new receipt(s) with their PDFs as paid by the company? They are kept as RTA's record of when and how each fine was paid. Nothing is sent to Ejar or RTA.`,
            confirmLabel: 'Save',
        }))) return;
        setBusy('save'); setError(null);
        try {
            const r = await finesService.uploadRtaReceipts(files, true, paidBy);
            changed.current = true;
            setSaved(r);
        } catch (e) { setError(errorMessage(e)); }
        setBusy('');
    };
    // the fines of these receipts to Excel at once (owner, 20 Oct 2026): the Paid fines Details columns, after a
    // Preview or a Save - the saved answer when there is one
    const exportExcel = async () => {
        setBusy('export'); setError(null);
        try { await finesService.exportReceiptFines((saved || preview)?.files || []); } catch (e) {
            // a refused file comes back as a Blob: the engine's own reason is inside it
            let msg = errorMessage(e);
            try { const d = JSON.parse(await e?.response?.data?.text?.()); if (d?.detail) msg = String(d.detail); } catch { /* not JSON */ }
            setError(msg);
        }
        setBusy('');
    };
    const close = () => { if (busy) return; if (changed.current) onDone?.(); onClose(); };

    return (
        <Dialog open onClose={close} size="xl" title="RTA payments - receipts" closeOnOverlay={!busy}>
            <div className="text-sm text-gray-600 mb-3">
                Upload RTA payment receipts (PDF) - above all the fines paid on RTA&apos;s public fines page, which never show in the
                company RTA account the engine reads every week - and Abu Dhabi TAMM receipts. They are read and matched to our fines first; <b>nothing is saved until you press Save</b>.
                RTA prints the company on every receipt, whoever paid: choose <b>Customer</b> for the receipts a customer sent you -
                their fines are marked paid by the customer.
            </div>
            {/* the buttons, the counts and any message stay in view while the receipts scroll under them */}
            <div className={`${stickyTop} mb-1`}>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                    <input type="file" multiple accept=".pdf,application/pdf" aria-label="Choose receipt PDFs" className="text-sm" disabled={!!busy}
                        onChange={(e) => { add(Array.from(e.target.files || [])); e.target.value = ''; }} />
                    {/* who paid them - chosen before anything is read: RTA prints the company on every receipt */}
                    <span className="inline-flex items-center gap-1 text-sm text-gray-700" role="group" aria-label="Who paid these receipts">
                        Paid by:
                        <Button size="sm" variant={paidBy === 'company' ? 'primary' : 'secondary'} aria-pressed={paidBy === 'company'}
                                disabled={!!busy} onClick={() => choosePaidBy('company')}>Company</Button>
                        <Button size="sm" variant={paidBy === 'customer' ? 'primary' : 'secondary'} aria-pressed={paidBy === 'customer'}
                                disabled={!!busy} onClick={() => choosePaidBy('customer')}>Customer</Button>
                    </span>
                    <Button size="sm" variant="primary" onClick={read} disabled={!files.length || !paidBy || !!problem || !!busy} isLoading={busy === 'read'}
                            leadingIcon={<Icon glyph={ICONS.search} size="sm" />}>
                        Preview
                    </Button>
                    <Button size="sm" variant="secondary" onClick={save} disabled={!preview || !toSave.length || !!saved || !!busy} isLoading={busy === 'save'}
                            leadingIcon={<Icon glyph={ICONS.save} size="sm" />}>
                        Save
                    </Button>
                    {canExport && (
                        <Button size="sm" variant="secondary" onClick={exportExcel} disabled={!preview || !readFiles.some((f) => f.ok) || !!busy}
                                isLoading={busy === 'export'} leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>
                            Export to Excel
                        </Button>
                    )}
                    <span className="text-sm text-gray-600">{files.length} file(s){preview ? ` · ${newRefs.length} new receipt(s)` : ''}</span>
                </div>
                {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                        {files.map((f) => (
                            <Pill key={fileKey(f)} tone={!isPdf(f) || f.size > MAX_BYTES ? 'red' : 'slate'} size="sm">
                                {f.name} · {sizeText(f.size)}
                                <button type="button" className="inline-flex items-center" disabled={!!busy} onClick={() => remove(fileKey(f))} aria-label={`Remove ${f.name}`}>
                                    <Icon glyph={ICONS.close} size="xs" />
                                </button>
                            </Pill>
                        ))}
                    </div>
                )}
                {problem && <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{problem}</div>}
                {error && <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                {saved && (
                    <div role="status" className="mb-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                        Saved {saved.summary?.new_receipts ?? 0} new receipt(s){customer ? ` as the customer's - ${saved.summary?.marked_customer ?? 0} fine(s) marked paid by the customer` : ''}. The Fines list shows who paid each fine, when and how.
                        {customer && (saved.summary?.on_ejar || []).length > 0 && (
                            <b> Already on their Ejar contract - remove these lines on Ejar: {saved.summary.on_ejar.join(', ')}.</b>
                        )}
                        {(saved.files || []).filter((f) => !f.ok).length > 0 && <> Not saved: {(saved.files || []).filter((f) => !f.ok).map((f) => `${f.name} (${f.error || 'unreadable'})`).join(', ')}.</>}
                    </div>
                )}
                {manyContracts.length > 0 && !saved && (
                    <div role="alert" className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        Check before saving as the customer&apos;s: {manyContracts.map((x) => `${x.name} pays fines of ${x.contracts.length} contracts (${x.contracts.join(', ')})`).join('; ')}.
                        A customer usually sends his own fines only - every fine on it will be marked paid by the customer.
                    </div>
                )}
                {preview && (
                    <div className="flex flex-wrap gap-2 text-sm">
                        <Pill tone="slate">Receipts: {s.receipts ?? readFiles.filter((f) => f.ok).length}</Pill>
                        <Pill tone="indigo">New: {s.new_receipts ?? newRefs.length}</Pill>
                        <Pill tone="slate">Fines: {s.fines ?? 0}</Pill>
                        <Pill tone="green">Matched: {s.matched ?? 0}</Pill>
                        <Pill tone={s.unknown ? 'red' : 'gray'}>Not in Yelo: {s.unknown ?? 0}</Pill>
                        <Pill tone={s.amount_diff ? 'amber' : 'gray'}>Amount differs: {s.amount_diff ?? 0}</Pill>
                        <Pill tone="slate">Impounds: {s.impounds ?? 0}</Pill>
                    </div>
                )}
            </div>
            {busy === 'read' && <LoadingSpinner message="Reading the receipts…" />}
            {preview && (
                <>
                    {failed.length > 0 && (
                        <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            Could not be read: {failed.map((f) => `${f.name} (${f.error || 'not an RTA receipt'})`).join(', ')}
                        </div>
                    )}
                    <Card className="mt-3" title="Receipts" subtitle="One row a PDF: when it was paid, by whom and how, as RTA printed it.">
                        <TableContainer maxHeight={260}>
                            <Table sticky>
                                <TableHead><TableRow>
                                    <TableHeader>#</TableHeader><TableHeader>File</TableHeader><TableHeader>Receipt ref</TableHeader><TableHeader>Paid on</TableHeader>
                                    <TableHeader>Received from</TableHeader><TableHeader>How</TableHeader><TableHeader>Total</TableHeader><TableHeader>Lines</TableHeader><TableHeader>In Yelo</TableHeader>
                                </TableRow></TableHead>
                                <TableBody>{readFiles.filter((f) => f.ok).map((f, i) => (
                                    <TableRow key={`${f.name}-${i}`}>
                                        <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                        <TableCell><CellText max={200}>{f.name}</CellText></TableCell>
                                        <TableCell><span title={f.printed_ref && f.printed_ref !== f.ref ? `printed ${f.printed_ref}` : undefined}><CellId>{f.ref || '—'}</CellId></span></TableCell>
                                        <TableCell><span className="ds-cell-date">{paidOn(f.paid_at) || '—'}</span></TableCell>
                                        {/* "company" is the engine's word (its payer name, or an uploaded receipt with no name = the owner on
                                            RTA's public page) - never decided here, so the dialog and the Fines list never disagree */}
                                        <TableCell><CellText>{payerText({ payer: f.payer, company: f.company, paid_by: f.paid_by, source: 'upload', paid_where: f.paid_where })}</CellText></TableCell>
                                        <TableCell><CellText>{howPaid(f) || '—'}</CellText></TableCell>
                                        <TableCell><CellMoney value={Number(f.total || 0)} currency="AED" /></TableCell>
                                        <TableCell><span className="ds-cell-num">{(f.lines || []).length}</span></TableCell>
                                        <TableCell>
                                            {f.already_stored ? <Pill tone="gray" size="sm">Already saved</Pill> : <Pill tone="indigo" size="sm">New</Pill>}
                                            {f.read_by_ai && <span title="No known receipt layout: the AI read it. Check the fines below before saving."> <Pill tone="amber" size="sm">Read by AI</Pill></span>}
                                        </TableCell>
                                    </TableRow>))}</TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                    {groups.map((g) => (
                        <Card key={g.key} className="mt-3" title={`${g.title} (${g.rows.length})`} subtitle={g.subtitle}
                              actions={<span className="text-sm text-gray-600">{fmtAED(g.rows.reduce((t, l) => t + (Number(l.amount) || 0), 0))}</span>}>
                            <LinesTable rows={g.rows} FineLink={FineLink} ContractLink={ContractLink} />
                        </Card>
                    ))}
                    {!groups.length && !failed.length && <div className="mt-3 text-sm text-gray-500">The receipts hold no fine or impound line.</div>}
                </>
            )}
            {modals}
        </Dialog>
    );
}
